import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createOrder } from "../../../features/order/orderSlice";
import toast from "react-hot-toast";

export default function CartSummary({ cartItems }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [showAgbError, setShowAgbError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const idempotencyKey = useRef(
    globalThis.crypto?.randomUUID?.() ||
      `checkout-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );

  const [street, setStreet] = useState("");
  const [zip, setZip] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");

  const totalWeight = useMemo(
    () =>
      cartItems.reduce(
        (sum, item) =>
          sum + Number(item.gewicht_kg || 0) * Number(item.menge || 0),
        0,
      ),
    [cartItems],
  );
  const shippingCost = useMemo(() => {
    if (totalWeight === 0) return 0;

    if (totalWeight <= 2) return 8.5;
    if (totalWeight <= 10) return 11.5;
    if (totalWeight <= 30) return 20.5;

    return 20.5;
  }, [totalWeight]);

  const itemsTotal = useMemo(
    () =>
      cartItems.reduce(
        (total, item) => total + item.Preis_brutto * item.menge,
        0,
      ),
    [cartItems],
  );

  const grandTotal = (itemsTotal + shippingCost).toFixed(2);

  const handleCheckout = async () => {
    if (isSubmitting) return;
    try {
      if (!agbAccepted) {
        setShowAgbError(true);
        return;
      }
      setShowAgbError(false);

      if (!user?.idUser) {
        toast.error("Bitte einloggen, um zu bestellen.");
        navigate("/login");
        return;
      }

      if (!street.trim() || !zip.trim() || !city.trim() || !country.trim()) {
        toast.error("Bitte Lieferadresse vollständig ausfüllen");
        return;
      }

      setIsSubmitting(true);

      const orderData = {
        idempotencyKey: idempotencyKey.current,
        shipping: {
          name: `${user.vorname ?? ""} ${user.nachname ?? ""}`.trim(),
          street: street.trim(),
          zip: zip.trim(),
          city: city.trim(),
          country: country.trim().toUpperCase(),
        },
        items: cartItems.map((item) => ({
          product_id: item.product_id,
          quantity: item.menge,
        })),
      };

      const orderResult = await dispatch(createOrder(orderData)).unwrap();

      if (orderResult?.success !== true) {
        toast.error(orderResult?.message || "Bestellung fehlgeschlagen");
        return;
      }

      const orderId = orderResult.order_id;
      const serverTotal = Number(orderResult.total_amount);

      if (!orderId || !Number.isFinite(serverTotal) || serverTotal <= 0) {
        console.error("orderResult:", orderResult);
        toast.error("Ungültige Bestelldaten vom Server");
        return;
      }

      const initResp = await axios.post(
        `${process.env.BACKEND_URL}/api/payment/saferpay/initialize`,
        { orderId },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        },
      );

      const { redirectUrl } = initResp.data || {};
      if (!redirectUrl) {
        console.error("Saferpay init response:", initResp.data);
        toast.error("Saferpay konnte nicht gestartet werden.");
        return;
      }

      window.location.href = redirectUrl;
    } catch (error) {
      console.error("Checkout Error:", error?.response?.data || error);
      toast.error("Checkout fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="kn-page flex flex-col gap-5">
      {/* Zahlungsübersicht */}
      <div className="kn-card p-[22px]">
        <h2 className="kn-legend mb-3.5">Zahlungsübersicht</h2>
        <div className="flex justify-between gap-4 text-[15px] py-2 text-knapp-ink2">
          <span>Zwischensumme</span>
          <strong className="font-semibold text-knapp-ink">
            CHF {itemsTotal.toFixed(2)}
          </strong>
        </div>
        <div className="flex justify-between gap-4 text-[15px] py-2 text-knapp-ink2">
          <span>Lieferkosten</span>
          <strong className="font-semibold text-knapp-ink">
            CHF {shippingCost.toFixed(2)}
          </strong>
        </div>
        <div className="flex justify-between gap-4 items-baseline mt-2.5 pt-3.5 border-t border-knapp-border">
          <span className="text-base font-semibold">Gesamtsumme</span>
          <strong className="text-[26px] font-bold tracking-[-0.02em] text-knapp-accent">
            CHF {grandTotal}
          </strong>
        </div>
        <div className="text-[13px] text-knapp-ink4 mt-1.5">inkl. MwSt.</div>
      </div>

      {/* Lieferadresse */}
      <div className="kn-card p-[22px]">
        <h2 className="kn-legend mb-4">Lieferadresse</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <label className="kn-field-label sm:col-span-2">
            Straße & Hausnummer
            <input
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              className="kn-input mt-1.5"
            />
          </label>

          <label className="kn-field-label">
            Postleitzahl
            <input
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              className="kn-input mt-1.5"
            />
          </label>

          <label className="kn-field-label">
            Ort
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="kn-input mt-1.5"
            />
          </label>

          <label className="kn-field-label sm:col-span-2">
            Land
            <input
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="kn-input mt-1.5"
            />
          </label>
        </div>
      </div>

      {/* Zahlung */}
      <div className="kn-card p-[22px]">
        <h2 className="kn-legend mb-3.5">Zahlungsmöglichkeiten</h2>

        <label
          htmlFor="agb"
          className="flex gap-2.5 items-start text-sm leading-relaxed text-knapp-ink2"
        >
          <input
            id="agb"
            type="checkbox"
            className="h-4 w-4 shrink-0 mt-0.5 accent-knapp-accent"
            checked={agbAccepted}
            onChange={(e) => setAgbAccepted(e.target.checked)}
          />
          <span>
            Ich habe die{" "}
            <a href="/agb" className="underline hover:no-underline">
              AGB
            </a>{" "}
            gelesen und akzeptiere sie.
          </span>
        </label>

        {showAgbError && !agbAccepted && (
          <p className="mt-2 text-sm text-knapp-accent">
            Bitte bestätigen Sie die AGB, um fortzufahren.
          </p>
        )}

        <motion.button
          className="kn-btn-primary w-full mt-4 py-[15px] text-base gap-2"
          initial={{ opacity: 0, y: 0 }}
          whileHover={!isSubmitting ? { scale: 1.02 } : undefined}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          onClick={handleCheckout}
          disabled={isSubmitting}
        >
          {isSubmitting && (
            <span
              className="inline-block h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin"
              aria-hidden="true"
            />
          )}
          {isSubmitting ? "Wird verarbeitet…" : "Zum Checkout"}
        </motion.button>
      </div>
    </div>
  );
}
